import React, { ReactNode, createContext, useState } from "react";

import { HeadNextJs } from "@/components/Header/HeadNextjs";
import { useSidebarMenus } from "@/hooks/useSidebarMenus";
import { Permission, PermissionValue } from "@/types/permissions";
import { generateBreadcrumbs } from "@/utils/breadcrumbs";
import { NextRouter, useRouter } from "next/router";

export const widthMenuHide = "80px";

export interface Menu {
  path?: string;
  title: string;
  icon: string;
  description?: string;
  applicationCapabilities?: string[];
  productRouteKey?: string;
  appId?: string;
  permissions?: (Permission | PermissionValue)[]; // Array of permission codenames required to access this menu
  roles?: string[];
  tenantKinds?: string[];
  hidden?: boolean; // If true, hides the menu item from the sidebar (but still used for breadcrumbs)
  underDevelopment?: boolean; // If true, renders a visible but non-navigable placeholder.
  subMenu?: Menu[];
  breadcrumb?: {
    // Whether to show this in breadcrumb (default: true)
    show?: boolean;
    // Custom title for breadcrumb (defaults to title)
    // Can be a string or a function that receives route params and returns a string
    title?: string | ((params: Record<string, string>) => string);
    // Whether to include dynamic segments in the breadcrumb (default: false)
    includeParams?: boolean;
  };
}
export interface BreadcrumbItem {
  title: string;
  path?: string;
}

export interface Breadcrumbs {
  breadcrumbs: (string | BreadcrumbItem)[];
  title: string;
}
type SidebarContext = {
  sidebarToggle: any;
  toggleSidebar: () => void;
  closeSidebar: () => void;
  getBreadcrumbsAndTitle: (router: NextRouter) => Breadcrumbs;
  menuState: boolean;
  setMenuState: React.Dispatch<React.SetStateAction<boolean>>;
  leftMenuList: Menu[];
  setLeftMenuList: React.Dispatch<React.SetStateAction<Menu[]>>;
};

export const SidebarContext = createContext<SidebarContext>({} as SidebarContext);

type Props = {
  children: ReactNode;
};

/**
 * getBreadcrumbsAndTitle: Returns human-readable breadcrumbs and title for the current route.
 * Uses the sidebar menu config to map route segments to menu titles.
 */
export function getBreadcrumbsAndTitle(router: NextRouter, menus: Menu[]) {
  // Generate breadcrumbs using the utility function
  const breadcrumbs = generateBreadcrumbs(menus, router);

  // Title is the last breadcrumb
  const title = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].title : "";

  return {
    breadcrumbs,
    title
  };
}

export function SidebarProvider({ children }: Props) {
  const [sidebarToggle, setSidebarToggle] = useState(false);
  const [menuState, setMenuState] = useState<boolean>(true); // state menu on full size screen
  const toggleSidebar = () => {
    setSidebarToggle(!sidebarToggle);
  };
  const menus = useSidebarMenus();
  const [leftMenuList, setLeftMenuList] = useState<Menu[]>(menus);

  const router = useRouter();
  const title = getBreadcrumbsAndTitle(router, menus).title;

  const closeSidebar = () => {
    setSidebarToggle(false);
  };

  return (
    <SidebarContext.Provider
      value={{
        sidebarToggle,
        toggleSidebar,
        closeSidebar,
        getBreadcrumbsAndTitle: (router: NextRouter) => getBreadcrumbsAndTitle(router, menus),
        menuState,
        setMenuState,
        leftMenuList,
        setLeftMenuList
      }}
    >
      <HeadNextJs title={title} />
      {children}
    </SidebarContext.Provider>
  );
}
